import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AppLayout from '@/components/feature/AppLayout';
import { useToast } from '@/contexts/ToastContext';
import { useKeyboardNav } from '@/utils/keyboardNav';
import { stockService, type StockStatus } from '@/services/stockService';
import { useAuth } from '@/contexts/AuthContext';
import { MODULES } from '@/utils/permissions';
import { useWarehouseStore } from '@/stores/warehouseStore';
import { getWarehousesForUser, type WarehouseResponse } from '@/api/warehouse.api';
import { serialApi, type SerialItem } from '@/api/serial.api';

const INVENTORY_STOCK_UPDATED_EVENT = 'inventory:stock-updated';

// ─── Types ────────────────────────────────────────────────────────────────────

interface WarehouseBreakdown {
  warehouseId: string;
  warehouseName: string;
  qty: number;
}

interface StockItem {
  id: string;
  name: string;
  code: string | null;
  unit: string | null;
  categoryName: string | null;
  brand: string | null;
  minStockLevel: number;
  totalStock: number;
  isSerialTracked?: boolean;
  isBatchTracked?: boolean;
  serialPrefix?: string | null;
  status: StockStatus;
  warehouseStocks: WarehouseBreakdown[];
}

interface WarehouseOption { id: string; name: string }

interface StockStats {
  totalItems: number;
  outOfStock: number;
  lowStock: number;
  normal: number;
  totalStockQty: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getStatus = (qty: number, min: number) => {
  if (qty === 0) return { label: 'Out of Stock', dot: 'bg-red-500', cls: 'bg-red-50    text-red-700   border-red-200' };
  if (qty < min) return { label: 'Low Stock', dot: 'bg-red-400', cls: 'bg-red-50    text-red-600   border-red-100' };
  if (qty === min) return { label: 'At Minimum', dot: 'bg-amber-400', cls: 'bg-amber-50  text-amber-700 border-amber-100' };
  return { label: 'Normal', dot: 'bg-emerald-500', cls: 'bg-emerald-50 text-emerald-700 border-emerald-100' };
};

const getWHStatus = (qty: number, min: number) => {
  if (qty === 0) return { icon: 'ri-close-circle-fill', color: 'text-red-500', label: 'Empty' };
  if (qty < min) return { icon: 'ri-error-warning-fill', color: 'text-amber-500', label: 'Low' };
  return { icon: 'ri-checkbox-circle-fill', color: 'text-emerald-500', label: 'OK' };
};

// ─── AdjustStockModal ─────────────────────────────────────────────────────────

interface AdjustModalProps {
  item: StockItem;
  warehouseOptions: WarehouseOption[];
  defaultWarehouseId?: string;
  onClose: () => void;
  onSave: (warehouseId: string, type: 'INCREASE' | 'DECREASE', qty: number, reason: string) => Promise<void>;
}

function AdjustStockModal({ item, warehouseOptions, defaultWarehouseId, onClose, onSave }: AdjustModalProps) {
  const formRef = useRef<HTMLDivElement>(null);
  useKeyboardNav(formRef);

  const [type, setType] = useState<'INCREASE' | 'DECREASE'>('INCREASE');
  const [warehouseId, setWarehouseId] = useState(
    defaultWarehouseId ?? item.warehouseStocks[0]?.warehouseId ?? warehouseOptions[0]?.id ?? '',
  );
  const [qty, setQty] = useState(1);
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const whStock = item.warehouseStocks.find((w) => w.warehouseId === warehouseId);
  const currentQty = whStock?.qty ?? 0;
  const afterQty = type === 'INCREASE' ? currentQty + qty : Math.max(0, currentQty - qty);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleSave = async () => {
    setError('');
    if (!warehouseId) return setError('Select a warehouse');
    if (qty <= 0) return setError('Quantity must be greater than 0');
    if (!reason.trim()) return setError('Reason is required');
    if (type === 'DECREASE' && qty > currentQty)
      return setError(`Cannot reduce by more than current stock in this warehouse (${currentQty})`);

    setSaving(true);
    try   { await onSave(warehouseId, type, qty, reason.trim()); }
    catch (err) { setError(err instanceof Error ? err.message : 'Failed to apply adjustment'); }
    finally { setSaving(false); }
  };

  const isIncrease = type === 'INCREASE';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-sm rounded-xl bg-white p-6 shadow-2xl">

        <div className="mb-4 flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-[#1e293b]">Adjust Stock</h3>
            <p className="mt-0.5 max-w-[240px] truncate text-xs text-[#64748b]">{item.name}</p>
          </div>
          <button onClick={onClose} className="flex h-7 w-7 items-center justify-center rounded-lg text-[#64748b] hover:bg-[#f1f5f9] cursor-pointer">
            <i className="ri-close-line" />
          </button>
        </div>

        {/* Before / After */}
        <div className="mb-4 flex items-center gap-3 rounded-lg bg-[#f8fafc] p-3">
          <div className="text-center">
            <p className="text-2xl font-bold text-[#1e293b]">{currentQty}</p>
            <p className="text-xs text-[#64748b]">{item.unit ?? 'Units'} · Before</p>
          </div>
          <div className="h-px flex-1 bg-[#e2e8f0]" />
          <i className="ri-arrow-right-line text-[#94a3b8]" />
          <div className="text-center">
            <p className={`text-2xl font-bold ${isIncrease ? 'text-emerald-600' : 'text-red-600'}`}>{afterQty}</p>
            <p className="text-xs text-[#64748b]">{item.unit ?? 'Units'} · After</p>
          </div>
        </div>

        <div ref={formRef} className="space-y-3">
          {/* Type toggle */}
          <div className="flex gap-2">
            {(['INCREASE', 'DECREASE'] as const).map((t) => (
              <button
                key={t} type="button" onClick={() => setType(t)}
                className={`flex-1 h-9 whitespace-nowrap rounded-lg border text-sm font-medium transition-colors cursor-pointer ${type === t
                    ? t === 'INCREASE' ? 'border-emerald-400 bg-emerald-50 text-emerald-700' : 'border-red-400 bg-red-50 text-red-700'
                    : 'border-[#e2e8f0] bg-white text-slate-500 hover:border-slate-300'
                  }`}
              >
                <i className={`${t === 'INCREASE' ? 'ri-arrow-up-line text-emerald-500' : 'ri-arrow-down-line text-red-500'} mr-1`} />
                {t === 'INCREASE' ? 'Add Stock' : 'Reduce Stock'}
              </button>
            ))}
          </div>

          {/* Warehouse */}
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-[#64748b]">Warehouse</label>
            <select value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}
              className="h-10 w-full cursor-pointer rounded-lg border border-[#e2e8f0] bg-white px-3 text-sm focus:border-[#4f46e5] focus:outline-none">
              {warehouseOptions.length === 0 && <option value="">No warehouses available</option>}
              {warehouseOptions.map((wh) => <option key={wh.id} value={wh.id}>{wh.name}</option>)}
            </select>
            {/* Show current stock for selected warehouse */}
            <p className="mt-1 text-xs text-slate-400">
              Current stock in this warehouse: <strong className="text-slate-600">{currentQty} {item.unit ?? 'units'}</strong>
            </p>
          </div>

          {/* Quantity */}
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-[#64748b]">
              Quantity <span className="text-red-500">*</span>
            </label>
            <input type="number" min="1" value={qty}
              onChange={(e) => { setQty(parseInt(e.target.value, 10) || 1); setError(''); }}
              className="h-10 w-full rounded-lg border border-[#e2e8f0] bg-white px-3 text-sm focus:border-[#4f46e5] focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20" />
          </div>

          {/* Reason */}
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-[#64748b]">
              Reason <span className="text-red-500">*</span>
            </label>
            <input type="text" value={reason}
              onChange={(e) => { setReason(e.target.value); setError(''); }}
              placeholder="Physical count, damage, purchase..."
              className="h-10 w-full rounded-lg border border-[#e2e8f0] bg-white px-3 text-sm focus:border-[#4f46e5] focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20" />
          </div>

          {error && (
            <p className="flex items-center gap-1 text-xs text-red-500">
              <i className="ri-error-warning-line" />{error}
            </p>
          )}
        </div>

        <div className="mt-5 flex gap-2">
          <button onClick={onClose}
            className="h-9 flex-1 rounded-lg border border-[#e2e8f0] text-sm font-medium text-[#64748b] hover:bg-[#f8fafc] cursor-pointer">
            Cancel
          </button>
          <button
            onClick={() => void handleSave()}
            disabled={saving}
            className={`flex h-9 flex-1 items-center justify-center gap-2 rounded-lg text-sm font-semibold text-white transition-colors disabled:opacity-60 cursor-pointer ${type === 'INCREASE' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'
              }`}
          >
            {saving ? <><i className="ri-loader-4-line animate-spin" /> Saving...</> : <><i className="ri-check-line" /> Apply</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── StatsCard ────────────────────────────────────────────────────────────────

function StatsCard({ label, value, icon, color }: { label: string; value: number; icon: string; color: string }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-[#e2e8f0] bg-white p-4">
      <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${color}`}>
        <i className={`${icon} text-lg`} />
      </div>
      <div>
        <p className="text-2xl font-bold text-[#1e293b]">{value.toLocaleString()}</p>
        <p className="text-xs text-[#64748b]">{label}</p>
      </div>
    </div>
  );
}

// ─── WarehouseSerialsModal ──────────────────────────────────────────────────

interface WarehouseSerialsModalProps {
  itemId: string;
  itemName: string;
  warehouseId: string;
  warehouseName: string;
  onClose: () => void;
}

function WarehouseSerialsModal({
  itemId,
  itemName,
  warehouseId,
  warehouseName,
  onClose,
}: WarehouseSerialsModalProps) {
  const [serials, setSerials] = useState<SerialItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [copiedSerial, setCopiedSerial] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    void (async () => {
      setLoading(true);
      try {
        const res = await serialApi.getAvailable(itemId, warehouseId);
        if (active && res.success) {
          setSerials(res.data || []);
        }
      } catch (err) {
        console.error('Failed to load serials:', err);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [itemId, warehouseId]);

  const filtered = useMemo(() => {
    if (!search.trim()) return serials;
    const q = search.trim().toLowerCase();
    const cleanQ = q.replace(/[^a-z0-9]/g, '');
    return serials.filter((s) => {
      const sn = (s.serial_number || '').toLowerCase();
      const vsn = (s.vendor_serial_number || '').toLowerCase();
      const imei = (s.imei || '').toLowerCase();
      if (sn.includes(q) || vsn.includes(q) || imei.includes(q)) return true;
      if (
        cleanQ &&
        (sn.replace(/[^a-z0-9]/g, '').includes(cleanQ) ||
          vsn.replace(/[^a-z0-9]/g, '').includes(cleanQ) ||
          imei.replace(/[^a-z0-9]/g, '').includes(cleanQ))
      ) {
        return true;
      }
      return false;
    });
  }, [serials, search]);

  const handleCopy = (sn: string) => {
    navigator.clipboard?.writeText(sn);
    setCopiedSerial(sn);
    setTimeout(() => setCopiedSerial(null), 1500);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-xs p-4">
      <div className="flex flex-col w-full max-w-2xl max-h-[85vh] rounded-2xl bg-white shadow-2xl overflow-hidden border border-slate-200">
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4 bg-slate-50">
          <div>
            <div className="flex items-center gap-2">
              <i className="ri-barcode-line text-blue-600 text-lg" />
              <h3 className="text-base font-bold text-slate-800">
                In-Stock Serials: {itemName}
              </h3>
            </div>
            <p className="text-xs text-slate-500 mt-0.5">
              Warehouse: <span className="font-semibold text-slate-700">{warehouseName}</span> · Total Available: <span className="font-semibold text-blue-600">{serials.length} units</span>
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-200 hover:text-slate-600 cursor-pointer"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        <div className="p-4 border-b border-slate-100 bg-white">
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-2.5 text-slate-400 text-sm" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search serial number, vendor SN, or IMEI..."
              className="w-full h-9 pl-9 pr-4 text-xs rounded-lg border border-slate-200 focus:outline-none focus:border-blue-500 font-mono"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {loading ? (
            <div className="py-12 text-center text-slate-400">
              <i className="ri-loader-4-line text-2xl animate-spin mb-2 block" />
              Loading available serials...
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-12 text-center text-slate-400">
              <i className="ri-barcode-box-line text-3xl mb-2 block text-slate-300" />
              No matching serials found in this warehouse.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {filtered.map((s) => (
                <div
                  key={s.id}
                  className="flex items-center justify-between p-3 rounded-xl border border-slate-100 bg-slate-50/60 hover:bg-white hover:border-blue-200 hover:shadow-xs transition-all"
                >
                  <div>
                    <div className="flex items-center gap-1.5">
                      <span className="font-mono text-xs font-bold text-slate-800 tracking-wider">
                        {s.serial_number}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleCopy(s.serial_number)}
                        title="Copy Serial Number"
                        className="text-slate-400 hover:text-blue-600 cursor-pointer text-xs"
                      >
                        <i className={copiedSerial === s.serial_number ? 'ri-check-line text-emerald-600' : 'ri-file-copy-line'} />
                      </button>
                    </div>
                    <div className="text-[10px] text-slate-400 mt-1 flex flex-wrap items-center gap-2">
                      <span>Status: <strong className="text-emerald-600">IN STOCK</strong></span>
                      {s.vendor_serial_number && (
                        <span>Vendor SN: <strong className="text-slate-600">{s.vendor_serial_number}</strong></span>
                      )}
                      {s.imei && (
                        <span>IMEI: <strong className="text-slate-600">{s.imei}</strong></span>
                      )}
                      {s.warranty_expiry_date && (
                        <span>Exp: {s.warranty_expiry_date.slice(0, 10)}</span>
                      )}
                    </div>
                  </div>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200">
                    Available
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between px-6 py-3 border-t border-slate-100 bg-slate-50 text-xs text-slate-500">
          <span>Showing {filtered.length} of {serials.length} serials</span>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-1.5 rounded-lg bg-white border border-slate-200 text-slate-700 font-medium hover:bg-slate-100 cursor-pointer"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function StockViewPage() {
  const toast = useToast();
  const { selectedWarehouseId } = useWarehouseStore();

  const [stockItems, setStockItems] = useState<StockItem[]>([]);
  const [stats, setStats] = useState<StockStats | null>(null);
  const [search, setSearch] = useState('');
  // FIX: warehouseFilter is kept in sync with the global store
  const [warehouseFilter, setWarehouseFilter] = useState(selectedWarehouseId ?? '');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [adjustItem, setAdjustItem] = useState<StockItem | null>(null);
  const [selectedSerialTarget, setSelectedSerialTarget] = useState<{
    itemId: string;
    itemName: string;
    warehouseId: string;
    warehouseName: string;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const { hasPermission, hasControl } = useAuth();
  const canAdjustStock = hasPermission(MODULES.STOCK_ADJUSTMENT, 'create') || hasControl('approveStockAdjustment');
  const canViewAll     = hasControl('viewAllWarehouses');

  const [warehousePageItems, setWarehousePageItems] = useState<WarehouseResponse[]>([]);

  useEffect(() => {
    let mounted = true;
    void (async () => {
      try {
        const res = await getWarehousesForUser(canViewAll);
        if (mounted && res.data) setWarehousePageItems(res.data);
      } catch { /* non-critical */ }
    })();
    return () => { mounted = false; };
  }, [canViewAll]);

  // Merge API warehouses + any seen in stock breakdown
  const warehouseOptions = useMemo<WarehouseOption[]>(() => {
    const merged = new Map<string, WarehouseOption>();
    for (const wh of warehousePageItems) {
      if (wh.is_active) merged.set(wh.id, { id: wh.id, name: wh.name });
    }
    for (const item of stockItems) {
      for (const bd of item.warehouseStocks) {
        if (!merged.has(bd.warehouseId))
          merged.set(bd.warehouseId, { id: bd.warehouseId, name: bd.warehouseName });
      }
    }
    return Array.from(merged.values());
  }, [warehousePageItems, stockItems]);

  const activeWarehouseName = warehouseOptions.find((w) => w.id === warehouseFilter)?.name;

  // ── Load stock ─────────────────────────────────────────────────────────────
  // FIX: warehouse_id passed to both getStock and getStockStats so the backend
  // scopes totals correctly. The original called both with no params.
  // FIX: wrapped in useCallback so it's stable for event listeners.
  const loadStock = useCallback(async (whId?: string, statusVal?: string) => {
    setLoading(true);
    setError('');

    const targetStatus = statusVal !== undefined ? statusVal : statusFilter;
    const params = {
      warehouse_id: (whId ?? warehouseFilter) || undefined,
      status: targetStatus !== 'ALL' ? targetStatus : undefined,
    };

    const statsParams = {
      warehouse_id: (whId ?? warehouseFilter) || undefined,
    };

    const [stockResult, statsResult] = await Promise.allSettled([
      stockService.getStock(params),
      stockService.getStockStats(statsParams),
    ]);

    console.log('📊 stockResult:', stockResult);
    console.log('📊 statsResult:', statsResult);

    if (stockResult.status === 'rejected') {
      const msg =
        stockResult.reason instanceof Error
          ? stockResult.reason.message
          : 'Failed to load stock data';
      console.error('❌ Stock load error:', msg);
      setError(msg);
      setStockItems([]);
      setStats(null);
      setLoading(false);
      return;
    }

    setStockItems((stockResult.value.data ?? []) as StockItem[]);
    setStats(statsResult.status === 'fulfilled' ? statsResult.value : null);
    setLoading(false);
  }, [warehouseFilter, statusFilter]);

  // Re-fetch whenever warehouse filter or status changes (also triggers initial load)
  useEffect(() => {
    void loadStock(warehouseFilter, statusFilter);
  }, [warehouseFilter, statusFilter, loadStock]);

  // External stock-update events (receiving, transfer, etc.)
  useEffect(() => {
    const handler = () => void loadStock(warehouseFilter, statusFilter);
    window.addEventListener(INVENTORY_STOCK_UPDATED_EVENT, handler);
    return () => window.removeEventListener(INVENTORY_STOCK_UPDATED_EVENT, handler);
  }, [loadStock, warehouseFilter, statusFilter]);

  // ─── Display qty (warehouse-aware) ─────────────────────────────────────────
  // ✅ FIX: extracted to useMemo per item to avoid stale closure in lowCount/outCount
  const displayQtyMap = useMemo<Map<string, number>>(() => {
    const map = new Map<string, number>();
    for (const item of stockItems) {
      if (!warehouseFilter) {
        map.set(item.id, item.totalStock);
      } else {
        const wh = item.warehouseStocks.find((w) => w.warehouseId === warehouseFilter);
        map.set(item.id, wh?.qty ?? item.totalStock);
      }
    }
    return map;
  }, [stockItems, warehouseFilter]);

  const getDisplayQty = useCallback((item: StockItem) => displayQtyMap.get(item.id) ?? item.totalStock, [displayQtyMap]);

  // Client-side search
  const filteredItems = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return stockItems;
    return stockItems.filter((item) =>
      [item.name, item.code, item.categoryName, item.brand]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(term)),
    );
  }, [stockItems, search]);

  // FIX: lowCount / outCount use getDisplayQty (warehouse-aware), not totalStock
  const lowCount = useMemo(
    () => filteredItems.filter((item) => {
      const q = getDisplayQty(item);
      return q > 0 && q <= item.minStockLevel;
    }).length,
    [filteredItems, getDisplayQty],
  );

  const outCount = useMemo(
    () => filteredItems.filter((item) => (displayQtyMap.get(item.id) ?? item.totalStock) === 0).length,
    [filteredItems, displayQtyMap],
  );

  // ─── Toggle row expand ──────────────────────────────────────────────────────
  const toggleExpand = (id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // ─── Adjustment handler ─────────────────────────────────────────────────────
  const handleAdjust = async (
    warehouseId: string, type: 'INCREASE' | 'DECREASE', qty: number, reason: string,
  ) => {
    if (!adjustItem) throw new Error('Select an item first');
    const targetId = adjustItem.id;

    const adjustment = await stockService.createAdjustment({
      warehouseId, itemId: targetId, type, quantity: qty, reason,
    });

    // Optimistic update
    setStockItems((prev) => prev.map((item) => {
      if (item.id !== targetId) return item;

      let found = false;
      const nextStocks = item.warehouseStocks.map((ws) => {
        if (ws.warehouseId !== warehouseId) return ws;
        found = true;
        return {
          ...ws,
          qty: typeof adjustment.meta?.updatedWarehouseQty === 'number'
            ? adjustment.meta.updatedWarehouseQty
            : type === 'INCREASE' ? ws.qty + qty : Math.max(0, ws.qty - qty),
        };
      });

      if (!found) {
        nextStocks.push({
          warehouseId,
          warehouseName: warehouseOptions.find((w) => w.id === warehouseId)?.name ?? 'Warehouse',
          qty: typeof adjustment.meta?.updatedWarehouseQty === 'number'
            ? adjustment.meta.updatedWarehouseQty
            : type === 'INCREASE' ? qty : 0,
        });
      }

      const totalStock = typeof adjustment.meta?.updatedItemTotalStock === 'number'
        ? adjustment.meta.updatedItemTotalStock
        : nextStocks.reduce((sum, s) => sum + s.qty, 0);

      return { ...item, warehouseStocks: nextStocks, totalStock };
    }));

    toast.success(`${adjustment.adjustmentNumber} saved for ${adjustment.itemName ?? adjustItem.name}`);
    setAdjustItem(null);

    // Background reconcile
    try { await loadStock(warehouseFilter); } catch { /* keep optimistic state */ }
  };

  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <AppLayout>
      <div className="h-[calc(100vh-4rem)] bg-[#f8fafc] p-6 flex flex-col gap-5">
        <div className="flex flex-col gap-5 shrink-0">

        {/* ── Header ── */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-[#1e293b]">Stock View</h1>
            <p className="mt-0.5 flex flex-wrap items-center gap-2 text-sm text-slate-500">
              <span>{filteredItems.length} item{filteredItems.length !== 1 ? 's' : ''}</span>
              {stats && <span className="text-slate-400">{stats.totalStockQty.toLocaleString()} total units</span>}
              {lowCount > 0 && (
                <span className="flex items-center gap-1 font-medium text-amber-600">
                  <i className="ri-alert-line text-xs" />{lowCount} low stock
                </span>
              )}
              {outCount > 0 && (
                <span className="flex items-center gap-1 font-medium text-red-600">
                  <i className="ri-error-warning-line text-xs" />{outCount} out of stock
                </span>
              )}
            </p>
          </div>
          <button onClick={() => void loadStock(warehouseFilter)} disabled={loading}
            className="flex h-9 items-center gap-1.5 rounded-lg border border-[#e2e8f0] px-3 text-sm text-[#64748b] hover:bg-[#f1f5f9] transition-colors disabled:opacity-50 cursor-pointer">
            <i className={`ri-refresh-line text-sm ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>

        {/* ── Stats cards ── */}
        {stats && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatsCard label="Total Items"   value={stats.totalItems}    icon="ri-stack-line"           color="bg-indigo-50 text-indigo-500" />
            <StatsCard label="Total Units"   value={stats.totalStockQty} icon="ri-archive-line"         color="bg-blue-50 text-blue-500"    />
            <StatsCard label="Low Stock"     value={stats.lowStock}      icon="ri-alert-line"           color="bg-amber-50 text-amber-500"  />
            <StatsCard label="Out of Stock"  value={stats.outOfStock}    icon="ri-error-warning-line"   color="bg-red-50 text-red-500"      />
          </div>
        )}

        {/* Warehouse filter banner */}
        {warehouseFilter && activeWarehouseName && (
          <div className="flex items-center gap-2 rounded-lg border border-indigo-100 bg-indigo-50 px-3 py-2 text-xs font-medium text-indigo-700">
            <i className="ri-store-3-line text-indigo-500" />
            Showing stock for <strong>{activeWarehouseName}</strong>
            <button
              onClick={() => setWarehouseFilter('')}
              className="ml-auto flex items-center gap-1 text-indigo-500 hover:text-indigo-700 cursor-pointer">
              <i className="ri-close-line" /> Show all warehouses
            </button>
          </div>
        )}

        </div>

        {/* ── Table card ── */}
        <div className="flex flex-col flex-1 min-h-0 rounded-xl border border-[#e2e8f0] bg-white">

          {/* Toolbar */}
          <div className="flex flex-wrap items-center gap-3 border-b border-[#e2e8f0] p-4 shrink-0">
            <div className="relative w-64">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400" />
              <input value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, code, category..."
                className="h-9 w-full rounded-lg border border-[#e2e8f0] pl-8 pr-3 text-sm focus:border-[#4f46e5] focus:outline-none" />
            </div>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="h-9 px-3 rounded-lg border border-[#e2e8f0] text-sm text-slate-600 bg-white focus:outline-none focus:border-[#4f46e5] cursor-pointer hover:border-slate-300 transition-colors"
            >
              <option value="ALL">All Stock Levels</option>
              <option value="NORMAL">Normal Stock</option>
              <option value="LOW_STOCK">Low Stock</option>
              <option value="OUT_OF_STOCK">Out of Stock</option>
            </select>

          </div>

          {/* Content */}
          {error ? (
            <div className="p-10 text-center text-slate-500">
              <i className="ri-error-warning-line mb-2 block text-3xl text-red-400" />
              <p>{error}</p>
              <button onClick={() => void loadStock(warehouseFilter)}
                className="mt-4 h-9 rounded-lg bg-[#4f46e5] px-4 text-sm font-medium text-white hover:bg-[#4338ca]">
                Retry
              </button>
            </div>
          ) : loading ? (
            <div className="p-10 text-center text-slate-500">
              <i className="ri-loader-4-line mb-2 block animate-spin text-3xl text-[#4f46e5]" />
              Loading stock data...
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-auto hover-scrollbar bg-white min-h-0 rounded-b-xl">
                <table className="w-full min-w-[1000px] text-sm">
                  <thead className="bg-slate-50 sticky top-0 z-10 shadow-sm">
                    <tr>
                    <th className="w-8 border-b border-[#e2e8f0] px-2 py-3" />
                    <th className="border-b border-[#e2e8f0] px-4 py-3 text-left  text-xs font-semibold whitespace-nowrap text-slate-500">Item</th>
                    <th className="border-b border-[#e2e8f0] px-4 py-3 text-left  text-xs font-semibold whitespace-nowrap text-slate-500">Code</th>
                    <th className="border-b border-[#e2e8f0] px-4 py-3 text-right text-xs font-semibold whitespace-nowrap text-slate-500">
                      {warehouseFilter && activeWarehouseName ? `${activeWarehouseName} Qty` : 'Total Stock'}
                    </th>
                    <th className="border-b border-[#e2e8f0] px-4 py-3 text-right text-xs font-semibold whitespace-nowrap text-slate-500">Min Level</th>
                    <th className="border-b border-[#e2e8f0] px-4 py-3 text-left  text-xs font-semibold whitespace-nowrap text-slate-500">Status</th>
                    <th className="border-b border-[#e2e8f0] px-4 py-3 text-center  text-xs font-semibold whitespace-nowrap text-slate-500">Action</th>
                    {canAdjustStock && <th className="border-b border-[#e2e8f0] px-4 py-3" />}
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.map((item, index) => {
                    const displayQty = getDisplayQty(item);
                    const status = getStatus(displayQty, item.minStockLevel);
                    const expanded = expandedRows.has(item.id);

                    return (
                      <Fragment key={item.id}>
                        <tr className={`group border-b border-slate-50 hover:bg-slate-50 transition-colors ${index % 2 === 1 ? 'bg-[#fafafa]' : ''}`}>

                          {/* Expand toggle */}
                          <td className="px-2 py-3 text-center">
                            <button
                              onClick={() => toggleExpand(item.id)}
                              title={expanded ? 'Hide breakdown' : 'Show warehouse breakdown'}
                              className={`flex h-6 w-6 items-center justify-center rounded transition-all cursor-pointer ${
                                expanded ? 'bg-indigo-100 text-[#4f46e5]' : 'text-slate-400 hover:bg-slate-100'
                              }`}>
                              <i className={`text-xs ${expanded ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line'}`} />
                            </button>
                          </td>

                          {/* Name & Tracking tags */}
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium text-[#1e293b]">{item.name}</span>
                              {item.isSerialTracked && (
                                <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200 font-semibold whitespace-nowrap">
                                  <i className="ri-barcode-line text-[10px]" />
                                  Serial Tracked
                                </span>
                              )}
                              {item.isBatchTracked && (
                                <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-purple-50 text-purple-700 border border-purple-200 font-semibold whitespace-nowrap">
                                  <i className="ri-archive-line text-[10px]" />
                                  Batch Tracked
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-slate-400 mt-0.5">{item.categoryName ?? item.brand ?? 'Uncategorized'}</div>
                          </td>

                          {/* Code */}
                          <td className="px-4 py-3 font-mono text-xs text-slate-500">{item.code ?? '—'}</td>

                          <td className={`px-4 py-3 text-right font-semibold ${displayQty === 0 ? 'text-red-600' : displayQty <= item.minStockLevel ? 'text-amber-600' : 'text-[#1e293b]'}`}>
                            {displayQty}
                            <span className="ml-1 text-xs font-normal text-slate-400">{item.unit ?? 'Units'}</span>
                          </td>

                          {/* Min */}
                          <td className="px-4 py-3 text-right text-slate-500">{item.minStockLevel}</td>

                          {/* Status badge */}
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium ${status.cls}`}>
                              <span className={`h-1.5 w-1.5 rounded-full ${status.dot}`} />
                              {status.label}
                            </span>
                          </td>

                          {/* Adjust button */}
                          {canAdjustStock && (
                            <td className="px-4 py-3">
                              <button
                                onClick={() => setAdjustItem(item)}
                                className="flex h-7 items-center gap-1.5 whitespace-nowrap rounded-lg border border-indigo-200 bg-indigo-50 px-2.5 text-xs font-medium text-[#4f46e5] opacity-0 transition-all hover:bg-indigo-100 cursor-pointer group-hover:opacity-100"
                              >
                                <i className="ri-scales-3-line text-sm" /> Adjust
                              </button>
                            </td>
                          )}
                        </tr>

                        {/* Warehouse breakdown */}
                        {expanded && (
                          <tr className="border-b border-slate-100 bg-[#f8fafc]">
                            <td />
                            <td colSpan={canAdjustStock ? 6 : 5} className="px-4 py-3">
                              <div className="ml-4 space-y-1.5">
                                <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                                  Warehouse Breakdown
                                </p>
                                {item.warehouseStocks.length === 0 ? (
                                  <div className="rounded-lg border border-dashed border-[#e2e8f0] bg-white px-3 py-2 text-sm text-slate-400">
                                    No warehouse stock recorded yet.
                                  </div>
                                ) : (
                                  item.warehouseStocks.map((ws) => {
                                    const whStatus = getWHStatus(ws.qty, item.minStockLevel);
                                    const isActive = ws.warehouseId === warehouseFilter;
                                    return (
                                      <div
                                        key={ws.warehouseId}
                                        className={`flex items-center gap-3 rounded-lg border px-3 py-2 transition-colors ${
                                          isActive ? 'border-indigo-200 bg-indigo-50/60' : 'border-[#e2e8f0] bg-white'
                                        }`}
                                      >
                                        <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-indigo-50">
                                          <i className="ri-store-3-line text-xs text-[#4f46e5]" />
                                        </div>
                                        <span className="flex-1 text-sm font-medium text-[#1e293b]">
                                          {ws.warehouseName}
                                          {isActive && (
                                            <span className="ml-2 text-[10px] font-bold text-indigo-500 bg-indigo-100 px-1.5 py-0.5 rounded-full">
                                              Active filter
                                            </span>
                                          )}
                                        </span>
                                        <span className={`text-sm font-semibold ${
                                          ws.qty === 0 ? 'text-red-600' : ws.qty < item.minStockLevel ? 'text-amber-600' : 'text-[#1e293b]'
                                        }`}>
                                          {ws.qty}
                                          <span className="text-xs font-normal text-slate-400 ml-1">{item.unit ?? 'Units'}</span>
                                        </span>
                                        <div className="flex items-center gap-1 text-xs">
                                          <i className={`${whStatus.icon} ${whStatus.color} text-base`} />
                                          <span className={`font-medium ${whStatus.color}`}>{whStatus.label}</span>
                                        </div>

                                        {/* View Serials button for serial-tracked items */}
                                        {item.isSerialTracked && ws.qty > 0 && (
                                          <button
                                            type="button"
                                            onClick={() =>
                                              setSelectedSerialTarget({
                                                itemId: item.id,
                                                itemName: item.name,
                                                warehouseId: ws.warehouseId,
                                                warehouseName: ws.warehouseName,
                                              })
                                            }
                                            className="ml-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 cursor-pointer transition-colors whitespace-nowrap"
                                          >
                                            <i className="ri-barcode-line text-xs" />
                                            View Serials ({ws.qty})
                                          </button>
                                        )}
                                      </div>
                                    );
                                  })
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>

              {filteredItems.length === 0 && (
                <div className="py-16 text-center text-slate-400">
                  <i className="ri-archive-line mb-2 block text-4xl" />
                  <p className="text-sm">No items found</p>
                  {(search || warehouseFilter) && (
                    <p className="mt-1 text-xs">
                      Try clearing your{search ? ' search' : ''}{search && warehouseFilter ? ' or' : ''}{warehouseFilter ? ' warehouse filter' : ''}
                    </p>
                  )}
                </div>
              )}
              </div>

              <div className="border-t border-[#e2e8f0] px-4 py-3 shrink-0">
                <p className="text-xs text-slate-400">
                  Showing {filteredItems.length} item{filteredItems.length !== 1 ? 's' : ''}
                  {warehouseFilter && activeWarehouseName ? ` in ${activeWarehouseName}` : ' across all warehouses'}
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      {adjustItem && (
        <AdjustStockModal
          item={adjustItem}
          warehouseOptions={warehouseOptions}
          defaultWarehouseId={
            warehouseFilter ||
            adjustItem.warehouseStocks[0]?.warehouseId ||
            warehouseOptions[0]?.id
          }
          onClose={() => setAdjustItem(null)}
          onSave={handleAdjust}
        />
      )}

      {selectedSerialTarget && (
        <WarehouseSerialsModal
          itemId={selectedSerialTarget.itemId}
          itemName={selectedSerialTarget.itemName}
          warehouseId={selectedSerialTarget.warehouseId}
          warehouseName={selectedSerialTarget.warehouseName}
          onClose={() => setSelectedSerialTarget(null)}
        />
      )}
    </AppLayout>
  );
}